/**
 * CoverCropperDialog — v3.0.145
 *
 * A reusable square-crop dialog powered by react-image-crop.
 * Given a File, opens a modal where the user can drag a square selection
 * over the image, then produces a fresh JPEG blob at outputSize x outputSize
 * that the parent can upload.
 *
 * Props:
 *   open, onOpenChange   — controlled from parent
 *   file: File | null    — the picked image file (dialog auto-closes if null)
 *   outputSize: number   — final canvas edge in px, default 1024
 *   onCropped(blob)      — called with the produced JPEG blob
 */
import { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Loader2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

function makeCroppedBlob(imgEl, cropPixels, outputSize) {
  return new Promise((resolve, reject) => {
    const scaleX = imgEl.naturalWidth / imgEl.width;
    const scaleY = imgEl.naturalHeight / imgEl.height;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      imgEl,
      cropPixels.x * scaleX,
      cropPixels.y * scaleY,
      cropPixels.width * scaleX,
      cropPixels.height * scaleY,
      0, 0, outputSize, outputSize,
    );
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      0.92,
    );
  });
}

export default function CoverCropperDialog({
  open, onOpenChange, file, aspect = 1, outputSize = 1024, onCropped,
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!file) { setImageSrc(null); return; }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop(null);
    setCompleted(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(
      makeAspectCrop({ unit: '%', width: 80 }, aspect, width, height),
      width, height,
    ));
  };

  const handleSave = async () => {
    if (!imgRef.current || !completed) return;
    setBusy(true);
    try {
      const blob = await makeCroppedBlob(imgRef.current, completed, outputSize);
      await onCropped(blob);
      onOpenChange(false);
    } catch (err) {
      console.error('crop save failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-white max-w-lg"
        data-testid="cover-cropper-dialog"
      >
        <DialogHeader>
          <DialogTitle>Crop your cover</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Drag the corners to resize. Drag inside to reposition. The square you keep is what everyone will see.
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex items-center justify-center bg-black rounded overflow-hidden" style={{ minHeight: '260px', maxHeight: '60vh' }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, pct) => setCrop(pct)}
              onComplete={(px) => setCompleted(px)}
              aspect={aspect}
              keepSelection
              minWidth={40}
              minHeight={40}
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt=""
                onLoad={onImageLoad}
                style={{ maxHeight: '55vh', maxWidth: '100%' }}
                data-testid="cover-cropper-image"
              />
            </ReactCrop>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="cover-cropper-cancel"
          >
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={busy || !completed || !completed.width}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="cover-cropper-save"
          >
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
            {busy ? 'Uploading' : 'Save cover'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
